import axios from 'axios'
import { apiClient } from '@/lib/api/client'

export type FeDeviceStatus = 'online' | 'idle' | 'offline'

export type FeLocationPing = {
  id: number
  fe_user_id: number
  fe_name?: string | null
  child_ticket_id?: number | null
  task_number?: string | null
  latitude: number
  longitude: number
  recorded_at: string
  device_status?: FeDeviceStatus | null
}

export type FeLocationPingFilters = {
  fe_user_id?: number
  online_only?: boolean
  since_minutes?: number
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message
    if (typeof message === 'string' && message.trim() !== '') {
      return message
    }
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

function mapPing(row: Record<string, unknown>): FeLocationPing {
  return {
    id: Number(row.id),
    fe_user_id: Number(row.fe_user_id ?? row.feUserId),
    fe_name: (row.fe_name ?? row.feName ?? row.field_engineer_name) as string | null | undefined,
    child_ticket_id:
      row.child_ticket_id != null
        ? Number(row.child_ticket_id)
        : row.childTicketId != null
          ? Number(row.childTicketId)
          : null,
    task_number: (row.task_number ?? row.taskNumber) as string | null | undefined,
    latitude: Number(row.latitude ?? row.lat),
    longitude: Number(row.longitude ?? row.lng),
    recorded_at: String(row.recorded_at ?? row.recordedAt ?? ''),
    device_status: (row.device_status ?? row.deviceStatus) as FeDeviceStatus | null | undefined,
  }
}

export const feLocationService = {
  async listRecentPings(filters: FeLocationPingFilters = {}): Promise<FeLocationPing[]> {
    try {
      const params: Record<string, string | number | boolean> = {}
      if (filters.fe_user_id != null) params.fe_user_id = filters.fe_user_id
      if (filters.online_only != null) params.online_only = filters.online_only ? 1 : 0
      if (filters.since_minutes != null) params.since_minutes = filters.since_minutes

      const response = await apiClient.get<{ success?: boolean; data?: FeLocationPing[]; message?: string }>(
        '/helpdesk/fe-location-pings',
        { params },
      )

      const rows = response.data?.data
      if (!Array.isArray(rows)) return []
      return rows.map((row) => mapPing(row as unknown as Record<string, unknown>))
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return []
      }
      throw new Error(apiErrorMessage(error, 'Failed to load FE location pings'))
    }
  },
}
